import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { debounce, MenuProps, SxProps } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { matchIsValidTel, MuiTelInput, MuiTelInputInfo } from 'mui-tel-input';
import { CountryCode, getExampleNumber, parsePhoneNumber } from 'libphonenumber-js';
import examples from 'libphonenumber-js/mobile/examples';
import { useTranslation } from 'react-i18next';

import GradientButton from 'components/GradienButton';
import { getIpInfo } from 'http-client/ip-info';
import { useAppData } from 'providers/RootStoreProvider';
import { useExtendedAdornmentArea } from './hooks';
import { ampli } from 'services/ampli';
import { blackList } from 'constants/blackList.const';

interface Props {
    redirectPath?: string;
    disabled?: boolean;
    initValue?: string;
    initPhoneInfo?: MuiTelInputInfo;
    sendValueCallback?: (value: MuiTelInputInfo) => void;
    isLanguageLoadedHandler?: (() => void) | null;
    onSubmit?: () => void;
    disableClickEventSend?: boolean;
}

const TelephoneInput = observer(
    ({ redirectPath, disabled, initValue, initPhoneInfo, sendValueCallback, isLanguageLoadedHandler, onSubmit, disableClickEventSend }: Props) => {
        const navigate = useNavigate();
        const inputRef = useExtendedAdornmentArea();
        const { t } = useTranslation();

        const [isInitialNumber, setIsInitialNumber] = useState(true);
        const [isValidInitValue, setIsValidInitValue] = useState<boolean | null>(!!(initValue && matchIsValidTel(initValue)));
        const [phoneValue, setPhoneValue] = useState(initValue || '');
        const [phoneInfo, setPhoneInfo] = useState<MuiTelInputInfo | Record<string, any> | undefined>(initPhoneInfo);
        const [exampleNumber, setExampleNumber] = useState('');

        const [isValidPhone, setIsValidPhone] = useState<boolean | null>(null);
        const { setSearchPhoneNumber, setSearchCountryCode, ipInfo, setIpInfo, flowOuter } = useAppData();
        const [loading, setLoading] = useState(!ipInfo && !isValidInitValue);

        const isValidated = isValidPhone !== null;

        useEffect(() => {
            if (initValue && !initPhoneInfo) {
                try {
                    const parsedNumber = parsePhoneNumber(initValue);
                    setPhoneInfo({
                        ...phoneInfo,
                        countryCode: parsedNumber.country,
                        numberValue: parsedNumber.number,
                    });
                } catch (error) {
                    console.error('Failed to parse phone number:', error);
                    setPhoneInfo(undefined);
                }
            }
        }, []);

        useEffect(() => {
            if (!ipInfo && !isValidInitValue) {
                setLoading(true);
                getIpInfo(flowOuter)
                    .then((response) => {
                        setIpInfo(response);
                    })
                    .finally(() => {
                        setTimeout(() => setLoading(false), 300);
                        if (isLanguageLoadedHandler) {
                            isLanguageLoadedHandler();
                        }
                    });
            }
        }, [ipInfo, initValue]);

        useEffect(() => {
            const currentCountryCode = phoneInfo?.countryCode;
            const placeholderCountryCode = currentCountryCode || ipInfo?.country;
            const currentExampleNumber = getExampleNumber(placeholderCountryCode as CountryCode, examples);
            if (currentExampleNumber) {
                setExampleNumber(currentExampleNumber.formatNational());
                setIsInitialNumber(false);
            }
        }, [phoneInfo?.countryCode, ipInfo?.country]);

        const handleChange = (newValue: string, info: MuiTelInputInfo) => {
            setIsValidPhone(null);
            setIsValidInitValue(isInitialNumber ? !!initValue : null);
            setPhoneValue(isInitialNumber && initValue ? initValue : newValue);
            setPhoneInfo(info);
            validatePhone(newValue, info);
            if (isInitialNumber) {
                setIsInitialNumber(false);
            }
        };

        const validatePhone = useCallback(
            debounce((phone: string, info: MuiTelInputInfo) => {
                if (!phone || phone === `+${info?.countryCallingCode}`) {
                    return;
                }
                const isValid = matchIsValidTel(phone);
                setIsValidPhone(isValid);
            }, 500),
            [matchIsValidTel]
        );

        const handleSend = () => {
            if (!phoneValue) return;
            const formattedPhone = phoneValue.replace(/\D/g, '');

            const isValid = matchIsValidTel(phoneValue) && !blackList.includes(formattedPhone);
            setIsValidPhone(isValid);
            if (isValid) {
                if (ampli.isLoaded && !disableClickEventSend) {
                    const params = {
                        success: true,
                        country_code: phoneInfo?.countryCode as string,
                        entered_number: phoneValue as string,
                        error_type: '',
                    };
                    ampli.numberLocateClicked(params);
                }

                setSearchPhoneNumber(phoneValue);
                setSearchCountryCode(phoneInfo?.countryCode as string);
                sendValueCallback?.(phoneInfo as MuiTelInputInfo);
                onSubmit?.();
                if (redirectPath) navigate(redirectPath);
            }
        };

        const phoneLength = !phoneValue ? 0 : phoneInfo?.numberValue?.length || phoneValue?.length - 2 || 0;

        const muiInputSx: SxProps = {
            mt: '26px',
            direction: 'ltr',
            '& .MuiTypography-root': {
                fontSize: '20px',
                fontWeight: '500',
                border: 'none',
            },
            '& input': {
                p: '19px 20px 19px 0px',
                fontSize: '20px',
                fontWeight: '300',
                lineHeight: '25px',
                color: !isValidated || isValidPhone ? '#0C0F21' : '#f44336',
            },
            '& .MuiInputBase-root': {
                borderRadius: '40px',
                bgcolor: '#fff',
                '& ::placeholder': { color: '#D3D3DD', opacity: '1' },
                '& :disabled::placeholder': {
                    WebkitTextFillColor: '#454B6F',
                },
            },
            '& .MuiInputAdornment-root': { position: 'relative', mr: '0px', color: '#0C0F21', height: '100%', visibility: loading ? 'hidden' : 'visible' },
            '& .MuiTelInput-Flag img': { minHeight: '24px', maxWidth: 'unset' },
            '& .MuiInputAdornment-root:after': {
                content: '""',
                display: 'block',
                position: 'absolute',
                cursor: 'pointer',
                inset: '-20px 0 -20px -14px',
            },
            '& .MuiInputAdornment-root:before': {
                content: 'url(/images/arrow.svg)',
                position: 'absolute',
                right: '4px',
                top: '50%',
                transform: 'translateY(-40%)',
            },
            '& .MuiTelInput-IconButton': {
                visibility: loading ? 'hidden' : 'visible',
                width: '24px',
                height: '24px',
                borderRadius: '50%',
                overflow: 'hidden',
                mr: '8px',
            },
            '& .MuiInputAdornment-root .MuiTypography-root': {
                pr: '23px',
            },
            '& .MuiOutlinedInput-input:-webkit-autofill': {
                WebkitBoxShadow: '0 0 0 1000px #fff inset',
                WebkitTextFillColor: '#0C0F21',
            },
            '& .Mui-disabled': {
                opacity: '0.7',
                borderRadius: '40px',
                '.MuiInputAdornment-root': { color: '#454B6F', mr: '0px' },
                '& .MuiInputAdornment-root:before': {
                    content: 'url(/images/arrow.svg)',
                    position: 'absolute',
                    right: '4px',
                    top: '50%',
                    transform: 'translateY(-40%)',
                    filter: 'invert(30%)',
                },
                fieldset: {
                    border: 'none',
                },
            },
            '& .MuiFormHelperText-root': {
                mt: '6px',
                mb: '6px',
                fontWeight: '300',
                lineHeight: '15px',
                color: '#FB5959',
            },
        };

        const muiMenuProps: Partial<MenuProps> = {
            sx: {
                '& .MuiPaper-root': {
                    marginTop: '4px',
                    maxHeight: '300px',
                    transitionDuration: '200ms !important',
                    scrollbarWidth: 'none',
                    color: '#0C0F21',
                    background: '#fff',
                    maxWidth: 'calc(100% - 40px)',
                    borderRadius: '14px',
                    border: '2px solid',
                    borderColor: 'text.lightBlue',
                },
                '& ::-webkit-scrollbar': {
                    display: 'none',
                },
                '& .MuiTelInput-ListItemIcon-flag': {
                    minWidth: 'unset !important',
                    marginRight: '16px',
                    '& img': {
                        width: '24px',
                        height: '24px',
                        overflow: 'hidden',
                        borderRadius: '100%',
                    },
                },
                '& .MuiTelInput-Typography-calling-code': { color: '#B2B2B2', fontSize: '16px', fontWeight: '300' },
                '& .MuiTelInput-ListItemText-country': {
                    '& .MuiTypography-root': {
                        color: 'rgba(45, 49, 55, 0.8)',
                        fontSize: '16px',
                        fontWeight: '500',
                    },
                },
                '& .MuiTelInput-MenuItem.Mui-selected, & .MuiTelInput-MenuItem.Mui-selected:hover': {
                    background: 'rgba(1, 207, 254, 0.30)',
                    width: 'calc(100% - 0.5px)',
                },
            },
            anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
        };

        return (
            <>
                <MuiTelInput
                    ref={inputRef}
                    flagSize="medium"
                    forceCallingCode
                    id="phone"
                    name="phone"
                    autoComplete="off"
                    placeholder={exampleNumber}
                    disabled={disabled}
                    value={phoneValue}
                    onChange={handleChange}
                    fullWidth
                    defaultCountry={ipInfo?.country || 'US'}
                    helperText={isValidated && !isValidPhone ? t('Wrong phone') : ''}
                    error={isValidated && !isValidPhone}
                    sx={muiInputSx}
                    inputProps={{
                        maxLength: 13,
                    }}
                    MenuProps={muiMenuProps}
                />
                <GradientButton
                    onClick={() => handleSend()}
                    disabled={!isValidInitValue && (phoneLength < 11 || !isValidated || !isValidPhone || disabled)}
                    sx={{ mt: '22px' }}
                >
                    {t('Locate')}
                </GradientButton>
                {/*<Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>*/}
                {/*    <CircularProgress color="inherit" />*/}
                {/*</Backdrop>*/}
            </>
        );
    }
);

export default TelephoneInput;
